import apiClient from './index';
import { invalidateSessionCache, withSessionCache } from './_cache';
import {
  Application,
  UscisFeeQuestionnaire,
  UscisFeeAnswer,
  UscisFeeComputation,
} from '../types/application';
import type { EligibilityFormQuestion } from './visaEligibilityAPI';

export interface EligibilityReportData {
  visaType: string;
  isEligible: boolean;
  questions: EligibilityFormQuestion[];
  parsedAnswers: { [questionId: number]: number | number[] };
}

export const getEligibilityReport = async (
  applicationId: number
): Promise<EligibilityReportData> => {
  try {
    const response = await apiClient.get<{ data: EligibilityReportData }>(
      `/applications/${applicationId}/eligibility-report`
    );
    return response.data.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || 'Failed to load eligibility report';
    console.error('API Error in getEligibilityReport:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

/**
 * Download the eligibility report as a server-generated, text-selectable PDF.
 *
 * The PDF is produced server-side by lgv-pdf-filler (compact vector text, ~100KB)
 * rather than the old browser screenshot path (html2canvas + jsPDF, ~10–12MB). The
 * caller must own the application; the backend authorizes via the auth token.
 * Triggers a browser download of the returned blob.
 */
export const downloadEligibilityReportPdf = async (
  applicationId: number
): Promise<void> => {
  try {
    const response = await apiClient.get(
      `/applications/${applicationId}/eligibility-report.pdf`,
      { responseType: 'blob' }
    );
    const blob =
      response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/pdf' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `eligibility-report-${applicationId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  } catch (error: any) {
    // Error responses arrive as a Blob when responseType is 'blob'; try to parse
    // the JSON message the backend sends (e.g. 502 RENDER_SERVICE_ERROR).
    let message = 'Failed to download eligibility report';
    const data = error?.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const json = JSON.parse(text);
        message = json?.message || message;
      } catch {
        // keep default message
      }
    } else if (typeof data?.message === 'string') {
      message = data.message;
    } else if (error?.message) {
      message = error.message;
    }
    console.error('API Error in downloadEligibilityReportPdf:', {
      message,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

/**
 * Fetch a single application by id.
 *
 * Short-TTL session-cached (30 s) to make back/forward navigation between
 * the overview page and form sections feel instant. Stage flips driven by
 * server-side transitions (payment completed, admin advances, etc.) become
 * visible at most ~30 s late, which we accept in exchange for the snappier
 * UX. Per-save endpoints (answers, readiness, feedback) are intentionally
 * NOT cached so anything the user just changed is always reflected
 * immediately. The cache is wiped on logout (see useLogout).
 */
export const getApplication = async (id: number): Promise<Application> => {
  return withSessionCache(
    `application:${id}`,
    async () => {
      try {
        const response = await apiClient.get<{ data: Application }>(`/applications/${id}`);
        return response.data.data;
      } catch (error: any) {
        const message =
          error?.response?.data?.message || error?.message || 'Failed to load application';
        console.error('API Error in getApplication:', {
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });
        throw new Error(message);
      }
    },
    { ttlMs: 30 * 1000 },
  );
};

const invalidateApplicationCache = (applicationId: number): void => {
  invalidateSessionCache(`application:${applicationId}`);
};

export const getUserApplications = async (): Promise<Application[]> => {
  try {
    const response = await apiClient.get<{ data: Application[] }>('/applications');
    return response.data.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || 'Failed to load applications';
    console.error('API Error in getUserApplications:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

export const selectPackage = async (
  applicationId: number,
  packageId: number
): Promise<Application> => {
  try {
    const response = await apiClient.put<{ data: Application }>(
      `/applications/${applicationId}/package`,
      { packageId }
    );
    invalidateApplicationCache(applicationId);
    return response.data.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.message || error?.message || 'Failed to select package';
    console.error('API Error in selectPackage:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

export interface UscisFeeDecisionResponse {
  application: Application;
  feeComputation?: UscisFeeComputation;
  checkoutUrl?: string;
}

export const setUscisFeeDecision = async (
  applicationId: number,
  optIn: boolean,
  answers?: UscisFeeAnswer[],
  successUrl?: string,
  cancelUrl?: string
): Promise<UscisFeeDecisionResponse> => {
  try {
    const payload: {
      optIn: boolean;
      answers?: UscisFeeAnswer[];
      successUrl?: string;
      cancelUrl?: string;
    } = { optIn };

    // If opting in, answers must be included to compute the fee before creating checkout session
    if (optIn && answers) {
      payload.answers = answers;
    }

    // When opting in, include successUrl and cancelUrl for Stripe checkout session
    // Backend requires these URLs to create the checkout session
    // If URLs are not provided, backend will log a warning and skip checkout creation
    if (optIn) {
      if (successUrl) {
        payload.successUrl = successUrl;
      }
      if (cancelUrl) {
        payload.cancelUrl = cancelUrl;
      }
    }

    console.log("API Call - setUscisFeeDecision:", {
      url: `/applications/${applicationId}/uscis-fee/decision`,
      method: "POST",
      payload,
    });

    const response = await apiClient.post<{
      message: string;
      data: UscisFeeDecisionResponse;
    }>(`/applications/${applicationId}/uscis-fee/decision`, payload);

    console.log("API Response - setUscisFeeDecision:", response.data);
    invalidateApplicationCache(applicationId);

    // Backend returns: { message, data: { application, feeComputation, checkoutUrl } }
    // Checkout session is only created if both successUrl and cancelUrl are provided
    if (response.data.data) {
      return response.data.data;
    } else {
      throw new Error("Unexpected response structure");
    }
  } catch (error: any) {
    console.error("API Error in setUscisFeeDecision:", {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
      config: error?.config,
    });
    throw error;
  }
};

export interface PayUscisFeeNowResponse {
  checkoutUrl: string;
  feeAmount: number;
  currency: string;
}

/**
 * Pay the USCIS fee on its own (the "pay later" flow for an applicant who opted
 * out earlier). Charges only the USCIS fee — not the package — and returns a
 * Stripe checkout URL to redirect to.
 */
export const payUscisFeeNow = async (
  applicationId: number,
  successUrl: string,
  cancelUrl: string
): Promise<PayUscisFeeNowResponse> => {
  const response = await apiClient.post<{ message: string; data: PayUscisFeeNowResponse }>(
    `/applications/${applicationId}/uscis-fee/pay-now`,
    { successUrl, cancelUrl }
  );
  invalidateApplicationCache(applicationId);
  return response.data.data;
};

export interface SubmissionReadinessMissingForm {
  formId: number | null;
  title: string;
  status: string;
}

export interface SubmissionReadinessMissingDocument {
  requirementId: number;
  category: string;
  description: string;
  minCount: number;
  uploadedCount: number;
}

export interface SubmissionReadiness {
  applicationId: number;
  stage: Application['stage'];
  submittedForReviewAt: string | null;
  alreadySubmitted: boolean;
  formsReady: boolean;
  documentsUnlocked: boolean;
  documentsReady: boolean;
  canSubmit: boolean;
  missingForms: SubmissionReadinessMissingForm[];
  missingDocuments: SubmissionReadinessMissingDocument[];
}

export interface SubmitForReviewResponse {
  application: Application;
  readiness: SubmissionReadiness;
  alreadySubmitted: boolean;
}

export const getSubmissionReadiness = async (
  applicationId: number
): Promise<SubmissionReadiness> => {
  try {
    const response = await apiClient.get<{ data: SubmissionReadiness }>(
      `/applications/${applicationId}/submission-readiness`
    );
    return response.data.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to check application readiness';
    console.error('API Error in getSubmissionReadiness:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

export const submitApplicationForReview = async (
  applicationId: number
): Promise<SubmitForReviewResponse> => {
  try {
    const response = await apiClient.post<{ data: SubmitForReviewResponse }>(
      `/applications/${applicationId}/submit-for-review`,
      {}
    );
    invalidateApplicationCache(applicationId);
    return response.data.data;
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error?.message ||
      error?.message ||
      'Failed to submit application for review';
    console.error('API Error in submitApplicationForReview:', {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw new Error(message);
  }
};

export const getUscisFeeQuestionnaire = async (
  applicationId: number
): Promise<UscisFeeQuestionnaire> => {
  const response = await apiClient.get<{ data: UscisFeeQuestionnaire }>(
    `/applications/${applicationId}/uscis-fee/questionnaire`
  );
  return response.data.data;
};

/**
 * Submit USCIS fee questionnaire answers.
 * Backend automatically computes the fee and returns checkout URL.
 */
export const submitUscisFeeQuestionnaire = async (
  applicationId: number,
  answers: UscisFeeAnswer[]
): Promise<{
  application: Application;
  feeComputation?: UscisFeeComputation;
  checkoutUrl?: string;
}> => {
  try {
    console.log("API Call - submitUscisFeeQuestionnaire:", {
      url: `/applications/${applicationId}/uscis-fee/submit`,
      method: "POST",
      payload: { answers },
    });

    const response = await apiClient.post<{
      data: {
        application: Application;
        feeComputation?: UscisFeeComputation;
        checkoutUrl?: string;
      };
    }>(`/applications/${applicationId}/uscis-fee/submit`, { answers });

    console.log("API Response - submitUscisFeeQuestionnaire:", response.data);
    invalidateApplicationCache(applicationId);

    // Handle different response structures
    if (response.data.data) {
      return response.data.data;
    } else {
      throw new Error("Unexpected response structure");
    }
  } catch (error: any) {
    console.error("API Error in submitUscisFeeQuestionnaire:", {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw error;
  }
};

/**
 * @deprecated Use submitUscisFeeQuestionnaire instead. 
 * Fee computation is now handled automatically by backend when questionnaire is submitted.
 */
export const computeUscisFee = async (
  applicationId: number,
  answers: UscisFeeAnswer[]
): Promise<{ application: Application; feeComputation: UscisFeeComputation; checkoutUrl?: string }> => {
  try {
    console.log("API Call - computeUscisFee:", {
      url: `/applications/${applicationId}/uscis-fee/compute`,
      method: "POST",
      payload: { answers },
    });
    
    const response = await apiClient.post<{
      data: { 
        application: Application; 
        feeComputation: UscisFeeComputation;
        checkoutUrl?: string;
      };
    }>(`/applications/${applicationId}/uscis-fee/compute`, { answers });
    
    console.log("API Response - computeUscisFee:", response.data);
    invalidateApplicationCache(applicationId);
    
    // Handle different response structures
    if (response.data.data) {
      return response.data.data;
    } else {
      throw new Error("Unexpected response structure");
    }
  } catch (error: any) {
    console.error("API Error in computeUscisFee:", {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw error;
  }
};

// Form types for API response
export interface FormData {
  form: {
    id: number;
    name: string;
    formType: string;
    [key: string]: any;
  };
  isRequired: boolean;
  status: string;
  isCompleted: boolean;
  /** Submitted feedback batch items still pending for this template form */
  feedbackPendingItemCount?: number;
  [key: string]: any;
}

export interface FormGroup {
  category: string;
  forms: FormData[];
}

export interface FormsResponse {
  forms: FormGroup[];
  flatForms: FormData[];
  formGroup?: string;
  uscisFormGroup?: string;
}

/**
 * Fetch forms for an application.
 * Returns forms grouped by category (Application, Supporting Document, etc.)
 */
export const getApplicationForms = async (
  applicationId: number
): Promise<FormsResponse> => {
  try {
    const response = await apiClient.get<{
      success: boolean;
      data: FormsResponse;
      count: number;
    }>(`/applications/${applicationId}/forms`);
    
    console.log("API Response - getApplicationForms:", response.data);
    
    if (response.data.data) {
      return response.data.data;
    } else {
      throw new Error("Unexpected response structure");
    }
  } catch (error: any) {
    console.error("API Error in getApplicationForms:", {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw error;
  }
};

/**
 * Fetch dynamic questions for a specific form by formId.
 * Backend endpoint: GET /forms/{formId}/questions
 *
 * Cached at the session-cache layer: the form definition (questions,
 * options, visibility rules) is the largest payload the form view pulls
 * and effectively static within a session. Network round-trip on first
 * visit; in-memory / sessionStorage on subsequent visits. Cache TTL is
 * 30 minutes and the entry is dropped on logout (see useLogout).
 */
export const getFormQuestions = async (formId: number): Promise<any> => {
  return withSessionCache(
    `form:${formId}:questions`,
    async () => {
      try {
        const response = await apiClient.get<{ data?: any; success?: boolean }>(
          `/forms/${formId}/questions`,
        );
        // Support both { data } and direct payload
        return (response.data as any)?.data ?? response.data;
      } catch (error: any) {
        console.error("API Error in getFormQuestions:", {
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });
        throw error;
      }
    },
    { persist: true, ttlMs: 30 * 60 * 1000 },
  );
};

/**
 * Submit form answers for an application.
 * Backend endpoint: POST /applications/{applicationId}/forms/{formId}/answers
 * 
 * @param applicationId - The application ID
 * @param formId - The form ID
 * @param answers - The form answers object where keys are question IDs/names
 */
export const submitFormAnswers = async (
  applicationId: number,
  formId: number,
  answers: Record<string, any>
): Promise<{ success: boolean }> => {
  try {
    const response = await apiClient.post<{ success: boolean }>(
      `/applications/${applicationId}/forms/${formId}/answers`,
      { answers }
    );
    return response.data;
  } catch (error: any) {
    // Check if it's a validation error (400 with errors array)
    if (error?.response?.status === 400 && error?.response?.data?.errors) {
      // Return the error object with validation errors to be handled by the caller
      const validationError = new Error(error.response.data.message || 'Validation failed');
      (validationError as any).validationErrors = error.response.data.errors;
      throw validationError;
    }

    console.error("API Error in submitFormAnswers:", {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw error;
  }
};

/**
 * Get form answers for an application.
 * Backend endpoint: GET /applications/{applicationId}/forms/{formId}/answers
 * 
 * @param applicationId - The application ID
 * @param formId - The form ID
 */
export const getFormAnswers = async (
  applicationId: number,
  formId: number
): Promise<Record<string, any>> => {
  try {
    const response = await apiClient.get<{ data?: any }>(
      `/applications/${applicationId}/forms/${formId}/answers`
    );
    return response.data.data || {};
  } catch (error: any) {
    console.error("API Error in getFormAnswers:", {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw error;
  }
};

/**
 * Mark a form as completed.
 * Backend endpoint: PUT /applications/{applicationId}/forms/{formId}/complete
 *
 * @param applicationId - The application ID
 * @param formId - The form ID
 */
export const completeForm = async (
  applicationId: number,
  formId: number
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await apiClient.put<{ success: boolean; message: string }>(
      `/applications/${applicationId}/forms/${formId}/complete`,
      {}
    );
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 400 && error?.response?.data?.message) {
      throw new Error(error.response.data.message);
    }

    console.error("API Error in completeForm:", {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw error;
  }
};

export interface FormReadinessMissingItem {
  questionId: number;
  categoryId: number | null;
  partNumber: string | null;
  itemNumber: string | null;
  label: string;
  message?: string;
}

/**
 * Per-form submission status, mirrored from the backend's ApplicationForm
 * row. Drives the user-portal lock UI: once `completed`, the form view
 * goes read-only until an admin opens a feedback batch.
 */
export type ApplicationFormStatus =
  | "not_started"
  | "in_progress"
  | "completed"
  | "submitted"
  | "reviewed"
  | "approved";

export interface FormReadiness {
  isReadyToSubmit: boolean;
  missing: FormReadinessMissingItem[];
  /**
   * Per-form status (added in Portal-Backend#108). May be missing on older
   * backends — callers should default to 'in_progress' / undefined.
   */
  applicationFormStatus?: ApplicationFormStatus;
  /** ISO timestamp when the form was last marked completed. */
  applicationFormCompletedAt?: string | null;
}

/**
 * Compute submission readiness for an application form.
 * A form is ready when every visible non-info question has a non-empty
 * saved answer; `missing` lists the offenders.
 *
 * Backend endpoint: GET /applications/{applicationId}/forms/{formId}/readiness
 */
export const getFormReadiness = async (
  applicationId: number,
  formId: number
): Promise<FormReadiness> => {
  try {
    const response = await apiClient.get<{ success: boolean; data: FormReadiness }>(
      `/applications/${applicationId}/forms/${formId}/readiness`
    );
    return response.data.data;
  } catch (error: any) {
    console.error("API Error in getFormReadiness:", {
      message: error?.message,
      response: error?.response?.data,
      status: error?.response?.status,
    });
    throw error;
  }
};

/**
 * The eligibility agreement (client worklist #79).
 *
 * The tier is decided by the server from its own evaluation — the client only
 * says which version of the wording it showed.
 */
export type EligibilityAcknowledgementStatus = {
  tier: "pass" | "warn" | "fail" | null;
  currentVersion: string;
  required: boolean;
  acknowledged: boolean;
};

export const getEligibilityAcknowledgement = async (
  applicationId: number
): Promise<EligibilityAcknowledgementStatus> => {
  const response = await apiClient.get<EligibilityAcknowledgementStatus>(
    `/applications/${applicationId}/eligibility-acknowledgement`
  );
  return response.data;
};

export const recordEligibilityAcknowledgement = async (
  applicationId: number,
  version: string
): Promise<void> => {
  await apiClient.post(
    `/applications/${applicationId}/eligibility-acknowledgement`,
    { version }
  );
};

